/**
 * Whether the funnel has any supplements to offer for the customer's effective
 * channel. The backend already filters supplement_rules.supplements by channel +
 * stock, so an empty list (or the kill switch off) means the Extras step has
 * nothing to show and must be skipped. Kept loose (structural) to avoid a type
 * import cycle.
 */
export const funnelOffersSupplements = (rules?: { enabled?: boolean; supplements?: unknown[] } | null): boolean =>
  rules?.enabled !== false && (rules?.supplements?.length ?? 0) > 0;

/**
 * Resolve the selected supplement UUIDs (product.id) for the checkout payload.
 * The funnel identifies a supplement by its `id` (UUID); the backend resolves
 * the per-channel product from the channel bindings, so the checkout sends
 * `selected_supplement_ids` (UUIDs), NOT Shopify variant ids.
 *
 * Filters to ids present in the config and deduplicates.
 */
export const getSelectedSupplementIds = (
  selectedSupplementIds: string[] = [],
  configSupplements?: { id: string }[],
): string[] => {
  if (!configSupplements || configSupplements.length === 0) {
    return Array.from(new Set(selectedSupplementIds));
  }
  const known = new Set(configSupplements.map((s) => s.id));
  return Array.from(new Set(selectedSupplementIds.filter((id) => known.has(id))));
};

/**
 * Resolve selected supplement UUIDs to the effective channel's variant ids.
 *
 * DEFERRED path only: the self-service "Mi Cuenta" add-on / update flows still
 * talk to `/account/*` keyed by Shopify variant id. On Shopify (the only platform
 * that reaches Mi Cuenta/Appstle) `external_variant_id` IS the Shopify variant, so
 * we resolve via it now that the app-config payload is channel-neutral. The public
 * funnel checkout no longer sends supplement variant ids (it sends UUIDs — see
 * getSelectedSupplementIds).
 */
export const getSupplementVariantIds = (
  selectedSupplementIds: string[] = [],
  configSupplements?: { id: string; external_variant_id?: string }[],
): string[] => {
  if (!configSupplements || configSupplements.length === 0) {
    console.warn('No config supplements available for variant ID resolution');
    return [];
  }

  const configMap: Record<string, string> = {};
  configSupplements.forEach((s) => {
    configMap[s.id] = s.external_variant_id ?? '';
  });

  const variantIds = selectedSupplementIds
    .map((id) => configMap[id])
    .filter((variantId): variantId is string => Boolean(variantId));

  return Array.from(new Set(variantIds));
};
